import { Injectable } from '@angular/core'
import { MediaService } from '@services/media.service'
import { Card, CardBack, CardService, CellType, Size } from './card.service'

export const storageKey: string = 'gameConfig'

export interface CardsGameConfig {
  sizeValue: string
  size: Size
  cardType: CellType
  back: CardBack
  showBeforeStart?: boolean
}

export const defaultGameConfig: CardsGameConfig = {
  sizeValue: '4x5',
  size: {
    cols: 4,
    rows: 5
  },
  cardType: 'number',
  back: 'moon'
}

export class Time {
  private timeout: NodeJS.Timeout | undefined = undefined;

  constructor() { }

  setTimeout(callback: (...args: any[]) => void, ms: number, ...args: any[]) {
    this.clearTimeout();
    this.timeout = setTimeout(callback, ms, args);
  }

  clearTimeout(): boolean {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    return this.reset();
  }

  reset(): boolean {
    if (this.timeout) {
      this.timeout = undefined;
      return true;
    }
    return false;
  }
}

export class CardGame {
  private readonly time: Time = new Time()

  constructor(
    public config: CardsGameConfig,
    private readonly cardService: CardService,
    private readonly media: MediaService,
    showBefore: boolean = false) {
    this.cards = this.generate(config, showBefore)
  }

  private _cards: Card[] = []

  get cards(): Card[] {
    return this._cards
  }

  set cards(cards: Card[]) {
    this._cards = cards
  }

  generate(config: CardsGameConfig, prepare: boolean = false) {
    const { cols, rows } = config.size
    // GENERATE CELLS
    return this.cardService.generateCells(
      cols,
      rows,
      prepare
    )
  }

  // LOGIC
  start() {
    if (!this.config.showBeforeStart) {
      this.showBefore() // Default 3 seconds
    }
  }

  showBefore(time: number = 3000) {
    this.cards.forEach(card => {
      card.active()
      card.select()
    })
    this.time.setTimeout(() => {
      this.media.play('shuffle')
      this.cards.forEach((card) => {
        card.deselect()
      })
    }, time)
  }

  select(card: Card) {

    if (this.time.clearTimeout()) {
      const selected = this.cards.filter(c => c.selected)

      if (this.check(selected)) {
        selected.forEach(c => c.complete())
      }

      this.clear(this.cards, card)
      return
    }

    card.toggle()

    const max = 2
    const selected = this.cards.filter(c => c.selected)
    const { length } = selected

    if (length) {
      this.media.play('second')
    }

    if (length === max) { // Check
      const same = this.check(selected)
      if (same) { // Mark as disabled
        this.media.vibrate([50, 100, 200])
        this.time.setTimeout(() => {
          selected.forEach(sc => {
            sc.complete()
          })
          this.checkEnd()
          this.time.reset()
          this.media.play('end')
        }, 420)
      } else {
        this.time.setTimeout(() => {
          this.clear(this.cards)
          this.time.reset()
          this.media.play('twice')
        }, 1000)
      }
    }

    if (length > max) {
      this.clear(this.cards, card)
    }
  }

  // HELPERS
  check(cards: Card[]): boolean {
    const { length } = cards
    if (length < 2) {
      return false
    }
    return cards[0].id === cards[1].id
  }

  checkEnd() {
    const [disabled, cards] = [
      this.cards.filter(card => card.disabled).length,
      this.cards.length
    ]
    if (disabled === cards) {
      this.media.play('coin');
      this.media.vibrate([500, 200, 100]);
      this.time.clearTimeout();
      this.cardService.complete();
    }
  }

  continue() {
    this.cards = this.generate(this.config, true)
    this.start()
  }

  clear(cards: Card[], selected?: Card) {
    cards.forEach(card => {
      card.deselect()
    })
    if (selected) {
      selected.select()
      this.media.play('first')
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class CardGameService {
  constructor(
    private cardService: CardService,
    private mediaService: MediaService) { }

  init() {
    const config = this.loadConfig(storageKey)
    const { showBeforeStart } = config
    config.size = this.getSize(config.sizeValue)

    return new CardGame(config, this.cardService, this.mediaService)
  }

  loadConfig(key?: string): CardsGameConfig {
    const config = localStorage.getItem(key || storageKey)
    if (config) {
      return JSON.parse(config) as CardsGameConfig
    }
    return defaultGameConfig
  }

  getSize(size: string): Size {
    const [columns, rows] = size
      .split('x')
      .map(el => +el)
    return {
      cols: columns,
      rows
    }
  }
}
